/* ==========================================================================
   Serinia – Produktseiten (nur dort geladen, defer)
   <sr-gallery>  Zähler + Balken beim Wischen, Zähler-Kontrast pro Bild,
                 Vergrösserung (Desktop)
   <sr-product>  Farbe (Section tauschen statt Seite laden), Grösse, Anzahl,
                 Preis, In den Warenkorb, Set, Zusatzartikel, Kaufleiste,
                 stehende Produktspalte (Desktop)
   Braucht sr-theme.js (window.Serinia: money, cart, Fenster).
   ========================================================================== */
(() => {
  if (customElements.get('sr-product')) return;
  const S = window.Serinia;
  const MAX_QTY = 9;

  /* --- Galerie ------------------------------------------------------------ */
  class SrGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.sr-gallery__track');
      this.initLightbox();
      this.bar = this.querySelector('.sr-gallery__bar');
      this.count = this.querySelector('.sr-gallery__count');
      if (!this.track || !this.bar || !this.count) return;
      this.dark = new WeakMap();
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', this.update, { once: true });
      });
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
      if (this.onLightboxResize) window.removeEventListener('resize', this.onLightboxResize);
    }

    /* --- Vergrösserung (nur Desktop): ganzes Bild im Fenster, Klick zoomt,
       die Maus verschiebt den Ausschnitt, Pfeile/Tasten blättern. Zeigt
       sofort das schon geladene Galeriebild und tauscht es gegen 2400 px. */
    initLightbox() {
      const dialog = this.querySelector('.sr-lightbox');
      const buttons = [...this.querySelectorAll('[data-sr-zoom]')];
      if (!dialog || !this.track || !buttons.length) return;
      const desktop = matchMedia('(min-width: 1024px)');
      const stage = dialog.querySelector('[data-sr-stage]');
      const img = dialog.querySelector('.sr-lightbox__img');
      const count = dialog.querySelector('.sr-lightbox__count');
      const sources = buttons.map((button) => button.parentElement.querySelector('img'));
      let index = 0;
      let opener = null;
      let pointer = null;
      let factor = 2;

      const large = (source) => {
        try {
          const url = new URL(source.currentSrc || source.src, location.href);
          url.searchParams.set('width', '2400');
          return url.toString();
        } catch (e) {
          return source.src;
        }
      };
      // Bild eingepasst in die Fläche innerhalb des Innenabstands
      const fit = () => {
        const ratio = parseFloat(buttons[index].dataset.srZoom) || 0.75;
        const css = getComputedStyle(stage);
        const w = stage.clientWidth - parseFloat(css.paddingLeft) - parseFloat(css.paddingRight);
        const h = stage.clientHeight - parseFloat(css.paddingTop) - parseFloat(css.paddingBottom);
        const width = Math.min(w, h * ratio);
        img.style.width = `${width}px`;
        img.style.height = `${width / ratio}px`;
      };
      const unzoom = () => {
        dialog.classList.remove('is-zoomed');
        img.style.transform = '';
      };
      // Gezoomt: Maus am linken Rand = linker Bildrand sichtbar, rechts = rechts
      const pan = () => {
        if (!dialog.classList.contains('is-zoomed') || !pointer) return;
        const w = img.offsetWidth;
        const h = img.offsetHeight;
        const sw = stage.clientWidth;
        const sh = stage.clientHeight;
        const zw = w * factor;
        const zh = h * factor;
        const rect = stage.getBoundingClientRect();
        const rx = Math.min(1, Math.max(0, (pointer.x - rect.left) / sw));
        const ry = Math.min(1, Math.max(0, (pointer.y - rect.top) / sh));
        const x = zw > sw ? -rx * (zw - sw) : (sw - zw) / 2;
        const y = zh > sh ? -ry * (zh - sh) : (sh - zh) / 2;
        img.style.transform = `translate(${x - img.offsetLeft}px, ${y - img.offsetTop}px) scale(${factor})`;
      };
      const show = (next) => {
        index = (next + sources.length) % sources.length;
        unzoom();
        const source = sources[index];
        img.src = source.currentSrc || source.src;
        img.alt = source.alt;
        count.textContent = sources.length > 1 ? `${index + 1}/${sources.length}` : '';
        fit();
        const shown = index;
        const big = new Image();
        big.src = large(source);
        big.decode().then(() => { if (index === shown && dialog.open) img.src = big.src; }).catch(() => {});
      };

      this.track.addEventListener('click', (event) => {
        const button = event.target.closest('[data-sr-zoom]');
        if (!button || !desktop.matches) return;
        opener = button;
        S.openDialog(dialog);
        show(buttons.indexOf(button));
      });
      stage.addEventListener('click', (event) => {
        if (dialog.classList.contains('is-zoomed')) return unzoom();
        if (event.target !== img) return S.closeDialog(dialog); // Klick daneben
        pointer = { x: event.clientX, y: event.clientY };
        // Mindestens bildschirmbreit, sonst bis zur vollen Auflösung (max. 3×)
        const w = img.offsetWidth;
        const full = Math.min(2400, Number(buttons[index].dataset.srWidth) || 2400);
        factor = Math.max(stage.clientWidth / w, Math.min(full / w, 3), 1.5);
        dialog.classList.add('is-zoomed');
        pan();
      });
      stage.addEventListener('mousemove', (event) => {
        pointer = { x: event.clientX, y: event.clientY };
        pan();
      });
      dialog.addEventListener('click', (event) => {
        const step = event.target.closest('[data-sr-step]');
        if (step) show(index + Number(step.dataset.srStep));
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') show(index - 1);
        else if (event.key === 'ArrowRight') show(index + 1);
      });
      dialog.addEventListener('close', () => {
        unzoom();
        opener?.focus({ preventScroll: true });
      });
      this.onLightboxResize = () => {
        if (!dialog.open) return;
        if (!desktop.matches) return S.closeDialog(dialog);
        fit();
        pan();
      };
      window.addEventListener('resize', this.onLightboxResize);
    }
    // Ist die Bildecke unten rechts (dort steht der Zähler) dunkel?
    isDark(img) {
      if (this.dark.has(img)) return this.dark.get(img);
      if (!img.complete || !img.naturalWidth) return false;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 24;
        canvas.height = 8;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        ctx.drawImage(img, w * 0.7, h * 0.88, w * 0.3, h * 0.12, 0, 0, 24, 8);
        const px = ctx.getImageData(0, 0, 24, 8).data;
        let sum = 0;
        for (let i = 0; i < px.length; i += 4) {
          const [r, g, b] = [px[i], px[i + 1], px[i + 2]].map((c) => {
            c /= 255;
            return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
          });
          sum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
        }
        const L = sum / (px.length / 4);
        const dark = 1.05 / (L + 0.05) > (L + 0.05) / 0.05;
        this.dark.set(img, dark);
        return dark;
      } catch (e) {
        this.dark.set(img, false); // fremde Domain ohne CORS: Zähler bleibt Tinte
        return false;
      }
    }
    update() {
      const slides = [...this.track.children];
      const total = Math.max(1, slides.length);
      const index = Math.min(total - 1, Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth)));
      this.bar.style.transform = `scaleX(${(index + 1) / total})`;
      this.count.textContent = `${index + 1}/${total}`;
      const img = slides[index]?.querySelector('img');
      this.count.classList.toggle('is-on-dark', img ? this.isDark(img) : false);
    }
  }
  customElements.define('sr-gallery', SrGallery);

  /* --- Abschnitts-HTML für einen Farbwechsel holen (mit Vorab-Laden) ------- */
  const sectionCache = new Map();
  const fetchSection = (url, sectionId) => {
    const target = new URL(url, location.href);
    target.searchParams.set('section_id', sectionId);
    const key = target.toString();
    if (!sectionCache.has(key)) {
      sectionCache.set(key, fetch(key).then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      }).catch((error) => {
        sectionCache.delete(key);
        throw error;
      }));
    }
    return sectionCache.get(key);
  };

  const labelOf = (input) => input?.closest('label')?.querySelector('[data-sr-option-label]')?.textContent.trim() || '';

  /* --- Produkt ------------------------------------------------------------- */
  class SrProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.sr-product__form');
      this.atc = this.querySelector('[data-sr-atc]');
      this.errorNote = this.querySelector('[data-sr-form-error]');
      this.isSet = this.hasAttribute('data-set');
      this.required = this.hasAttribute('data-required');
      this.pct = Number(this.dataset.salePercent) || 0;

      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPrefetch = this.onPrefetch.bind(this);

      if (this.form) {
        this.form.noValidate = true;
        this.form.addEventListener('submit', this.onSubmit);
        this.form.addEventListener('change', this.onChange);
      }
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerenter', this.onPrefetch, true);
      this.addEventListener('touchstart', this.onPrefetch, { passive: true, capture: true });
      this.addEventListener('focusin', this.onPrefetch);

      this.querySelectorAll('[data-sr-qty]').forEach((qty) => this.syncQty(qty));
      this.refresh();
      this.initSticky();
      this.initBuybar();
      this.initUpsells();
    }

    disconnectedCallback() {
      this.stickyObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.buybar?.remove();
      this.buybarObservers?.forEach((o) => o.disconnect());
      document.removeEventListener('sr:cart-changed', this.onCartChanged);
    }

    /* Preise und Beschriftungen aus der aktuellen Auswahl */
    refresh() {
      if (!this.form) return;
      let price = 0;
      let compare = 0;
      let chosen = true;

      if (this.isSet) {
        this.querySelectorAll('[data-sr-set-row]').forEach((row) => {
          const input = row.querySelector('.sr-select__input:checked');
          const qty = Number(row.querySelector('.sr-qty__value')?.value) || 1;
          const optional = row.hasAttribute('data-optional');
          const value = row.querySelector('[data-sr-row-value]');
          const qtyRow = row.querySelector('.sr-select__qty');
          if (!input?.value) {
            if (optional) {
              value.textContent = S.strings.optionalAdd || value.textContent;
              if (qtyRow) qtyRow.hidden = true;
            }
            return;
          }
          if (qtyRow) qtyRow.hidden = false;
          price += Number(input.dataset.price) * qty;
          compare += Number(input.dataset.compare || input.dataset.price) * qty;
          value.textContent = labelOf(input) + (qty > 1 ? ` · ${S.strings.quantity || 'Anzahl'} ${qty}` : '');
        });
      } else {
        // ohne Auswahlzeile (nur eine Variante): Liquid hat schon alles gesetzt
        if (!this.form.querySelector('input[type="radio"][name="id"]')) return;
        const input = this.form.querySelector('input[name="id"]:checked');
        const qty = Number(this.form.querySelector('.sr-qty__value')?.value) || 1;
        const value = this.querySelector('[data-sr-picker-value]');
        chosen = Boolean(input);
        if (input) {
          price = Number(input.dataset.price) * qty;
          const c = Number(input.dataset.compare) || 0;
          compare = c > Number(input.dataset.price) ? c * qty : 0;
          if (value) value.textContent = labelOf(input) + (qty > 1 ? ` · ${S.strings.quantity || 'Anzahl'} ${qty}` : '');
        } else if (value) {
          value.textContent = S.strings.choose;
        }
      }

      // Set nicht bestellbar (Liquid: Pflichtteil fehlt): Set-Preis stehen lassen
      const setUnavailable = this.isSet && this.atc?.hasAttribute('data-sold-out');
      if (chosen && (price > 0 || this.isSet) && !setUnavailable) {
        const priceEl = this.querySelector('[data-sr-price]');
        if (priceEl) priceEl.textContent = S.money(price);
        const showSale = compare > price;
        const compareEl = this.querySelector('[data-sr-compare]');
        if (compareEl) {
          compareEl.hidden = !showSale;
          compareEl.querySelector('[data-sr-compare-value]').textContent = showSale ? S.money(compare) : '';
        }
        const sale = this.querySelector('[data-sr-sale]');
        if (sale) {
          sale.hidden = !showSale;
          if (showSale) {
            sale.querySelector('[data-sr-saved]').textContent = `${S.money(compare - price)} ${S.strings.saved || 'gespart'}`;
            sale.querySelector('[data-sr-sale-pct]').textContent = this.pct || Math.round(((compare - price) / compare) * 100);
          }
        }
      }

      if (this.atc && !this.atc.hasAttribute('data-sold-out')) {
        if (!chosen) {
          this.atc.textContent = S.strings.chooseSize;
          this.atc.disabled = false;
        } else if (this.isSet && price === 0) {
          this.atc.disabled = true;
        } else {
          this.atc.disabled = false;
          this.atc.textContent = `${S.strings.addToCart} · ${S.money(price)}`;
        }
      }
      this.syncBuybar?.();
    }

    onChange(event) {
      const input = event.target;
      this.hideError();
      if (input.matches('input[name="id"][type="radio"]')) {
        // Adresse zeigt die gewählte Variante (teilbar, Zurück-Taste)
        const url = new URL(location.href);
        url.searchParams.set('variant', input.value);
        history.replaceState(history.state, '', url);
        if (this.required) {
          const row = input.closest('details');
          if (row?.open) S.toggleDetails(row, false);
        }
      }
      this.refresh();
    }

    onClick(event) {
      const step = event.target.closest('[data-sr-qty] [data-step]');
      if (step) {
        const qty = step.closest('[data-sr-qty]');
        const field = qty.querySelector('.sr-qty__value');
        field.value = Math.min(MAX_QTY, Math.max(1, Number(field.value) + Number(step.dataset.step)));
        this.syncQty(qty);
        this.refresh();
        return;
      }
      const swatch = event.target.closest('a[data-sr-swap]');
      if (swatch && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        if (swatch.getAttribute('aria-current') !== 'true') this.swap(swatch.href);
      }
    }

    syncQty(qty) {
      const value = Number(qty.querySelector('.sr-qty__value').value) || 1;
      const [minus, plus] = qty.querySelectorAll('[data-step]');
      if (minus) minus.disabled = value <= 1;
      if (plus) plus.disabled = value >= MAX_QTY;
    }

    onPrefetch(event) {
      const swatch = event.target.closest?.('a[data-sr-swap]');
      if (swatch && swatch.getAttribute('aria-current') !== 'true') {
        fetchSection(swatch.href, this.dataset.sectionId).catch(() => {});
      }
    }

    /* --- Farbe wechseln: Section austauschen, Auswahl mitnehmen ------------ */
    snapshot() {
      return {
        rows: [...this.querySelectorAll('.sr-select')].map((row) => ({
          label: labelOf(row.querySelector('.sr-select__input:checked')),
          none: row.querySelector('.sr-select__input:checked')?.value === '',
          qty: row.querySelector('.sr-qty__value')?.value,
          open: row.open,
        })),
      };
    }

    restore(state) {
      const rows = [...this.querySelectorAll('.sr-select')];
      state.rows.forEach((saved, i) => {
        const row = rows[i];
        if (!row) return;
        const inputs = [...row.querySelectorAll('.sr-select__input')];
        if (saved.none) {
          inputs.find((input) => input.value === '')?.click();
        } else if (saved.label) {
          const match = inputs.find((input) => labelOf(input) === saved.label && !input.disabled);
          if (match) match.checked = true;
        } else if (this.required) {
          inputs.forEach((input) => { input.checked = false; });
        }
        const qty = row.querySelector('.sr-qty__value');
        if (qty && saved.qty) {
          qty.value = saved.qty;
          this.syncQty(row.querySelector('[data-sr-qty]'));
        }
        row.open = saved.open;
      });
      this.refresh();
    }

    async swap(url) {
      const state = this.snapshot();
      this.setAttribute('aria-busy', 'true');
      try {
        const html = await fetchSection(url, this.dataset.sectionId);
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('sr-product');
        if (!fresh) throw new Error('no section');
        this.replaceWith(fresh);
        fresh.restore(state);

        // Adresse: Farbe (Produkt oder Variante); gewählte Grösse mitnehmen
        const next = new URL(url, location.href);
        const checked = fresh.querySelector('input[name="id"]:checked');
        if (checked) next.searchParams.set('variant', checked.value);
        history.replaceState(history.state, '', next);
        document.title = fresh.dataset.title || document.title;

        fresh.querySelector('.sr-gallery__track')?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 350, easing: 'ease-out' });
        fresh.querySelector('a[data-sr-swap][aria-current="true"]')?.focus({ preventScroll: true });
        S.initMotion?.(fresh);
      } catch (error) {
        location.href = url; // Fallback: ganz normal laden
      }
    }

    /* --- In den Warenkorb ---------------------------------------------------- */
    items() {
      if (this.isSet) {
        const rows = [...this.querySelectorAll('[data-sr-set-row]')];
        const chosen = rows
          .map((row) => ({ row, input: row.querySelector('.sr-select__input:checked') }))
          .filter(({ input }) => input?.value);
        const group = chosen.map(({ input }) => input.value).join('-');
        return chosen.map(({ row, input }) => {
          const properties = { _bundle_group: group };
          row.querySelectorAll('input[type="hidden"][name*="[properties]"]').forEach((field) => {
            const key = field.name.match(/\[properties\]\[([^\]]+)\]/)?.[1];
            if (key && key !== '_bundle_group' && field.value) properties[key] = field.value;
          });
          return {
            id: Number(input.value),
            quantity: Number(row.querySelector('.sr-qty__value')?.value) || 1,
            properties,
          };
        });
      }
      const input = this.form.querySelector('input[name="id"]:checked') || this.form.querySelector('input[type="hidden"][name="id"]');
      if (!input?.value || (input.type === 'radio' && !input.checked)) return [];
      return [{ id: Number(input.value), quantity: Number(this.form.querySelector('.sr-qty__value')?.value) || 1 }];
    }

    async onSubmit(event) {
      event.preventDefault();
      const items = this.items();
      if (!items.length) {
        // Grösse fehlt: Zeile öffnen und hinscrollen
        const row = this.querySelector('[data-sr-picker]');
        if (row) {
          if (!row.open) S.toggleDetails(row, true);
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          row.querySelector('.sr-select__input:not(:disabled)')?.focus({ preventScroll: true });
        }
        return;
      }
      this.atc.setAttribute('aria-busy', 'true');
      try {
        await S.cart.add(items);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.atc.removeAttribute('aria-busy');
      }
    }

    showError(text) {
      if (!this.errorNote) return;
      this.errorNote.textContent = text;
      this.errorNote.hidden = false;
    }

    hideError() {
      if (this.errorNote) this.errorNote.hidden = true;
    }

    /* --- Desktop: Produktspalte bleibt stehen, solange die Galerie scrollt.
       Höher als der Bildschirm: erst mitscrollen, bis das Ende sichtbar ist. */
    initSticky() {
      const column = this.querySelector(':scope > .sr-product');
      if (!column) return;
      const header = document.querySelector('.sr-header-section');
      const GAP = 24;
      this.onResize = () => {
        const below = (header?.offsetHeight || 0) + GAP;
        const fit = window.innerHeight - column.offsetHeight - GAP;
        column.style.setProperty('--sr-sticky-top', `${Math.min(below, fit)}px`);
      };
      this.stickyObserver = new ResizeObserver(this.onResize);
      this.stickyObserver.observe(column);
      window.addEventListener('resize', this.onResize);
      this.onResize();
    }

    /* --- Kaufleiste: erscheint, wenn der Button nach oben weg ist ---------- */
    initBuybar() {
      if (!this.atc) return;
      const bar = document.createElement('div');
      bar.className = 'sr-buybar';
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', this.querySelector('.sr-product__title')?.textContent.trim() || '');
      bar.innerHTML = '<p class="sr-buybar__info"><span></span><span></span></p><button class="sr-button" type="button" tabindex="-1"></button>';
      document.body.append(bar);
      this.buybar = bar;
      const [name, price] = bar.querySelectorAll('.sr-buybar__info span');
      const button = bar.querySelector('button');
      name.textContent = this.querySelector('.sr-product__title')?.textContent.trim() || '';

      this.syncBuybar = () => {
        price.textContent = this.querySelector('[data-sr-price]')?.textContent.trim() || '';
        const label = this.atc.textContent.trim();
        button.textContent = label.split(' · ')[0];
        button.disabled = this.atc.disabled;
      };
      this.syncBuybar();

      // Zur Auswahl scrollen statt direkt in den Warenkorb – oft stimmt noch
      // eine Grösse oder Anzahl nicht
      button.addEventListener('click', () => {
        const target = this.querySelector('.sr-colors') || this.querySelector('.sr-selects') || this.form;
        const header = document.querySelector('.sr-header-section');
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - (header?.offsetHeight || 0) - 16, behavior: 'smooth' });
        const row = this.querySelector('[data-sr-picker]');
        if (row && !this.items().length && !row.open) S.toggleDetails(row, true);
      });

      let atcAbove = false;
      let footerIn = false;
      const update = () => {
        const show = atcAbove && !footerIn;
        bar.classList.toggle('is-visible', show);
        button.tabIndex = show ? 0 : -1;
      };
      const atcObserver = new IntersectionObserver(([entry]) => {
        atcAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      });
      atcObserver.observe(this.atc);
      this.buybarObservers = [atcObserver];
      const footer = document.querySelector('.sr-footer');
      if (footer) {
        const footerObserver = new IntersectionObserver(([entry]) => { footerIn = entry.isIntersecting; update(); });
        footerObserver.observe(footer);
        this.buybarObservers.push(footerObserver);
      }
    }

    /* --- Zusatzartikel: Fenster von unten, im Hintergrund in den Warenkorb -- */
    initUpsells() {
      this.onCartChanged = (event) => {
        const cart = event.detail?.cart;
        if (!cart) return;
        this.querySelectorAll('[data-sr-upsell]').forEach((card) => {
          const remove = card.querySelector('[data-sr-upsell-remove]');
          if (remove.dataset.key && !cart.items.some((item) => item.key === remove.dataset.key)) this.setUpsell(card, null);
        });
      };
      document.addEventListener('sr:cart-changed', this.onCartChanged);

      this.querySelectorAll('[data-sr-upsell]').forEach((card) => {
        const sheet = document.getElementById(card.dataset.srUpsell);
        if (!sheet) return;
        const add = sheet.querySelector('[data-sr-sheet-add]');
        const colorName = sheet.querySelector('[data-sr-sheet-color]');
        const groups = [...sheet.querySelectorAll('[data-sr-sheet-group]')];

        const showGroup = () => {
          const color = sheet.querySelector('input[type="radio"][name$="-color"]:checked')?.value;
          if (colorName) colorName.textContent = color || '';
          groups.forEach((group) => {
            const active = groups.length === 1 || group.dataset.srSheetGroup === color;
            group.hidden = !active;
            if (!active) group.querySelectorAll('input').forEach((input) => { input.checked = false; });
          });
          updateAdd();
        };
        const updateAdd = () => {
          const size = sheet.querySelector('input[name$="-size"]:checked');
          add.disabled = !size;
          add.textContent = size ? `${S.strings.add || 'Hinzufügen'} · ${S.money(Number(size.dataset.price))}` : S.strings.chooseSize;
        };
        sheet.addEventListener('change', (event) => {
          if (event.target.name.endsWith('-color')) showGroup();
          else updateAdd();
        });
        showGroup();

        add.addEventListener('click', async () => {
          const size = sheet.querySelector('input[name$="-size"]:checked');
          if (!size) return;
          add.setAttribute('aria-busy', 'true');
          const error = sheet.querySelector('[data-sr-sheet-error]');
          try {
            const data = await S.cart.add([{ id: Number(size.value), quantity: 1 }], { open: false });
            this.setUpsell(card, { key: data.items?.[0]?.key, label: size.dataset.label });
            // Bestätigung im Button, dann Fenster zu und Hinweis unter dem Header
            add.textContent = S.strings.inCart;
            await new Promise((resolve) => setTimeout(resolve, 650));
            S.closeDialog(sheet);
            updateAdd();
            const name = data.items?.[0]?.product_title || '';
            S.notifyAdded([name, size.dataset.label].filter(Boolean).join(' · '));
          } catch (e) {
            if (error) {
              error.textContent = e.message;
              error.hidden = false;
            }
          } finally {
            add.removeAttribute('aria-busy');
          }
        });

        card.querySelector('[data-sr-upsell-remove]')?.addEventListener('click', async (event) => {
          const key = event.currentTarget.dataset.key;
          this.setUpsell(card, null);
          if (key) await S.cart.change(key, 0).catch(() => {});
        });
      });
    }

    setUpsell(card, added) {
      const open = card.querySelector('.sr-upsell__open');
      const state = card.querySelector('.sr-upsell__added');
      const remove = card.querySelector('[data-sr-upsell-remove]');
      open.hidden = Boolean(added);
      state.hidden = !added;
      remove.dataset.key = added?.key || '';
      if (added) card.querySelector('[data-sr-upsell-summary]').textContent = added.label || '';
      (added ? remove : open).focus({ preventScroll: true });
    }
  }
  customElements.define('sr-product', SrProduct);
})();
